/**
 * MarkdownText — lightweight inline markdown renderer for comments.
 *
 * Supports:
 * - Auto-linked URLs (bare https://... and http://...)
 * - Markdown links: [text](url)
 * - Bold: **text**
 * - Italic: *text* (single word or phrase, requires non-space after opening *)
 * - Inline code: `code`
 * - Code blocks: ```code```
 * - Strikethrough: ~~text~~
 *
 * Deliberately does NOT match:
 * - _underscores_ — too many false positives with variable names, URLs, etc.
 * - * at start of line as italic — that's a bullet point, not emphasis
 *
 * No external dependencies. Renders to React elements with proper escaping
 * (no dangerouslySetInnerHTML). Safe against XSS by construction.
 */

import React from 'react';

interface MarkdownTextProps {
  readonly text: string;
}

/**
 * Parses a single line of text and returns React elements with markdown rendered.
 * Uses a fresh regex per call to avoid stale lastIndex issues.
 */
function parseLine(text: string): React.ReactNode[] {
  // Order matters: longer/more specific patterns first to avoid partial matches.
  // - Code blocks (```) before inline code (`)
  // - Bold (**) before italic (*)
  // - Markdown links before bare URLs
  // - __ intentionally excluded (too many false positives)
  // - Single _ intentionally excluded (too many false positives)
  // - @mentions styled as highlighted tags
  const regex =
    /(```[\s\S]*?```)|(`[^`\n]+`)|(\[([^\]]+)\]\((https?:\/\/[^\s)]+)\))|(https?:\/\/[^\s<>)\]]+)|(\*\*([^*]+?)\*\*)|((?<=^|[\s(])\*([^*\s](?:[^*]*[^*\s])?)\*(?=[\s.,;:!?)}\]]|$))|(~~([^~]+?)~~)|(@[a-zA-Z][a-zA-Z0-9 _-]{0,49}(?=[\s.,;:!?)\]}]|$))/g;

  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    // Push any plain text before this match
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }

    const fullMatch = match[0];

    if (match[1]) {
      // Code block: ```...```
      const code = match[1].slice(3, -3).trim();
      nodes.push(
        <pre key={key++} className="mt-1 mb-1 p-2 rounded bg-muted text-xs font-mono overflow-x-auto whitespace-pre-wrap">
          <code>{code}</code>
        </pre>
      );
    } else if (match[2]) {
      // Inline code: `...`
      const code = match[2].slice(1, -1);
      nodes.push(
        <code key={key++} className="px-1 py-0.5 rounded bg-muted text-xs font-mono">
          {code}
        </code>
      );
    } else if (match[3]) {
      // Markdown link: [text](url)
      const linkText = match[4];
      const url = match[5];
      nodes.push(
        <a
          key={key++}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:text-primary/80"
        >
          {linkText}
        </a>
      );
    } else if (match[6]) {
      // Bare URL
      const url = cleanTrailingPunctuation(match[6]);
      nodes.push(
        <a
          key={key++}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:text-primary/80 break-all"
        >
          {url}
        </a>
      );
      // If we trimmed trailing punctuation, adjust lastIndex so it stays as plain text
      const trimmed = match[6].length - url.length;
      if (trimmed > 0) {
        lastIndex = match.index + fullMatch.length - trimmed;
        continue;
      }
    } else if (match[7]) {
      // Bold: **text**
      nodes.push(<strong key={key++}>{match[8]}</strong>);
    } else if (match[9]) {
      // Italic: *text* (with word boundary guards)
      nodes.push(<em key={key++}>{match[10]}</em>);
    } else if (match[11]) {
      // Strikethrough: ~~text~~
      nodes.push(<s key={key++}>{match[12]}</s>);
    } else if (match[13]) {
      // @mention
      nodes.push(
        <span key={key++} className="text-primary font-medium">
          {match[13]}
        </span>
      );
    } else {
      // Fallback — render as plain text
      nodes.push(fullMatch);
    }

    lastIndex = match.index + fullMatch.length;
  }

  // Push any remaining text after the last match
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  // If nothing matched at all, just return the original text
  if (nodes.length === 0) {
    return [text];
  }

  return nodes;
}

/**
 * Strips trailing punctuation that's likely not part of a URL.
 * e.g., "https://example.com." → "https://example.com"
 */
function cleanTrailingPunctuation(url: string): string {
  return url.replace(/[.,;:!?]+$/, '');
}

/**
 * Renders text with basic markdown formatting.
 * Preserves line breaks and whitespace — plain text without any markdown
 * renders exactly the same as before (whitespace-pre-wrap).
 */
export function MarkdownText({ text }: MarkdownTextProps): React.ReactElement {
  const lines = (text ?? '').split('\n');

  return (
    <span className="whitespace-pre-wrap break-words">
      {lines.map((line, i) => (
        <React.Fragment key={i}>
          {i > 0 && '\n'}
          {parseLine(line)}
        </React.Fragment>
      ))}
    </span>
  );
}
